import React, { ReactNode, useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { usePublicRuntimeConfigValue } from '../../lib/config';
import { track } from '@spotify-internal/track-js';
import dynamic from 'next/dynamic';
import { BasePageProps } from '../../lib/basePageProps';

const Header = dynamic(
  () => import('@spotify-internal/masthead-react').then(mod => mod.Header),
  { ssr: true },
);

const Footer = dynamic(
  () => import('@spotify-internal/masthead-react').then(mod => mod.Footer),
  { ssr: true },
);
import '@spotify-internal/landing-page-library/esm/styles/masthead/index.css';
import '@spotify-internal/masthead-react/index.css';

type TrackJsConfig = {
  enabled: boolean;
  googleTagManagerId?: string;
};

export function BasePage({
  basePageProps: {
    masthead,
    translationLocale,
    formattingLocale,
    direction,
    market,
    oneTrust,
  },
  children,
}: {
  basePageProps: BasePageProps;
  children: ReactNode;
}) {
  const { enabled: trackJsEnabled, googleTagManagerId } =
    usePublicRuntimeConfigValue<TrackJsConfig>('analytics.trackJs');

  useEffect(() => {
    if (!trackJsEnabled) {
      return;
    }

    track({
      market,
      language: translationLocale,
      googleTagManagerId,
      oneTrust,
    });
  }, [oneTrust, googleTagManagerId, translationLocale, market, trackJsEnabled]);

  useEffect(() => {
    document.documentElement.lang = translationLocale;
    document.documentElement.dir = direction;
  }, [translationLocale, direction]);

  return (
    <IntlProvider locale={formattingLocale} defaultLocale="en">
      {masthead?.header && (
        <Header
          fixed={false}
          transparent={false}
          {...(masthead.header as React.ComponentPropsWithoutRef<
            typeof Header
          >)}
        />
      )}
      <main
        className="encore-dark-theme"
        dir={direction}
        lang={translationLocale}
      >
        {children}
      </main>
      {masthead?.footer && (
        <Footer
          {...(masthead.footer as React.ComponentPropsWithoutRef<
            typeof Footer
          >)}
        />
      )}
    </IntlProvider>
  );
}

export default BasePage;
